/**
 * Admin Premium-signup queries — paying-subscriber events.
 *
 * North Star KPI on the admin dashboard since the 2026-04-30 metric pivot
 * (post-soft-launch). Pre-launch this slot was held by landing-page email
 * signups; post-launch it's actual paying conversions.
 *
 * "Premium signup" = a profile that flipped to is_premium AND has a real
 * paying subscription_plan (monthly / annual / founding). Admin / test
 * grants set is_premium without setting subscription_plan, so they're
 * excluded by the subscription_plan filter.
 *
 * 2026-05-17 audit fix: previous filter was `.not('stripe_subscription_id',
 * 'is', null)` but NOTHING in the codebase writes that column — every paying
 * customer was excluded and this query returned 0 rows. Swapped to
 * `subscription_plan IS NOT NULL` which is actually written by the
 * stripe-webhook on `checkout.session.completed`.
 */
import { supabase } from '@/lib/supabase'
import { ADMIN_PREMIUM_SIGNUPS_LIMIT } from '../_constants'

/**
 * Fetch all profiles that became Premium subscribers via Stripe, newest
 * first by `premium_since`. Excludes admin / test / no-plan grants.
 *
 * Returns rows shaped for the dashboard's Recent list + daily-bar chart:
 * id, name pieces (whichever the user filled in), email, the timestamp
 * the profile flipped to premium, and the plan they're on.
 *
 * Capped at ADMIN_PREMIUM_SIGNUPS_LIMIT (300). If conversions ever exceed
 * that, swap to a paginated fetch — the dashboard's daily-bar chart only
 * needs the recent slice anyway.
 */
export async function fetchPremiumSignups() {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, email, first_name, full_name, premium_since, subscription_plan')
    .eq('is_premium', true)
    .not('subscription_plan', 'is', null)
    .not('premium_since', 'is', null)
    .order('premium_since', { ascending: false })
    .limit(ADMIN_PREMIUM_SIGNUPS_LIMIT)
  if (error) throw error
  return data || []
}
